import React from "react";
import RequestEngine from "../../core/RequestEngine";
import strings from "../../core/translate";
import { apiErrorMessage } from "../../shared/api-error";
import {
  FormSection,
  FormGrid,
  Field,
  TextField,
  SelectField,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";
import PermissionMatrix from "../../components/Form/PermissionMatrix";

// Who the account is, which garage it is limited to, and exactly what it may
// view and edit.
export default class UserAccessForm extends React.Component {
  constructor(props) {
    super(props);
    const account = props.item || {};
    const user = account.user || {};

    this.state = {
      id: account.id || "",
      version: account.version,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      password: "",
      branchId: account.branch ? account.branch.id : "",
      roleId: account.role ? account.role.id : "",
      scopes: account.scopes || account.scope || [],
      roles: [],
      branches: [],
      loading: false,
      error: "",
      errors: {},
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.fetchRoles();
    this.fetchBranches();
  }

  fetchRoles = async () => {
    const response = await this.engine.getItem("roles");
    if (response && response.status === 200) {
      this.setState({
        roles: response.data.items.map((role) => ({
          value: role.id,
          label: role.name,
          scopes: role.scopes || [],
        })),
      });
    }
  };

  fetchBranches = async () => {
    const { merchantId } = this.props;
    if (!merchantId) return;
    const response = await this.engine.getItem(
      `merchants/${merchantId}/branches`
    );
    if (response && response.status === 200) {
      this.setState({
        branches: [
          { value: "", label: strings.all_garages },
          ...response.data.items.map((branch) => ({
            value: branch.id,
            label: branch.name || branch.address,
          })),
        ],
      });
    }
  };

  // picking a role loads its permissions as the starting point
  handleRoleSelect = (roleId) => {
    const role = this.state.roles.find((item) => item.value === roleId);
    this.setState({
      roleId,
      scopes: role ? [...role.scopes] : this.state.scopes,
    });
  };

  validate() {
    const { id, firstName, lastName, email, phoneNumber, password, scopes } =
      this.state;
    const errors = {};

    if (!id) {
      if (`${firstName}`.trim().length < 3) errors.firstName = strings.name_min3_hint;
      if (`${lastName}`.trim().length < 3) errors.lastName = strings.name_min3_hint;
      if (`${phoneNumber}`.trim().length < 8) errors.phoneNumber = strings.phone_min_hint;
      if (`${password}`.length < 8) errors.password = strings.password_hint;
    }
    if (!/^\S+@\S+\.\S+$/.test(`${email}`.trim())) {
      errors.email = strings.email_hint;
    }
    if (!scopes || scopes.length === 0) {
      errors.scopes = strings.permissions_required_hint;
    }
    return errors;
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const {
      id,
      version,
      firstName,
      lastName,
      email,
      phoneNumber,
      password,
      branchId,
      roleId,
      scopes,
    } = this.state;
    const { merchantId } = this.props;

    this.setState({ loading: true, error: "", errors: {} });
    const pathname = `merchants/${merchantId}/users`;

    try {
      let response;
      if (id) {
        // access only: the account details keep their own screen
        response = await this.engine.updateItem(pathname, id, {
          version,
          scopes,
          branchId: branchId || null,
          roleId: roleId || null,
        });
      } else {
        response = await this.engine.saveItem(pathname, {
          user: {
            firstName: `${firstName}`.trim(),
            lastName: `${lastName}`.trim(),
            email: `${email}`.trim(),
            phoneNumber: `${phoneNumber}`.trim(),
            password,
          },
          scopes,
          ...(branchId && { branchId }),
          ...(roleId && { roleId }),
        });
      }

      if (response && (response.status === 200 || response.status === 201)) {
        this.setState({ loading: false });
        this.props.onSaved(response.data);
        return;
      }
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
    this.setState({ loading: false });
  };

  render() {
    const {
      id,
      firstName,
      lastName,
      email,
      phoneNumber,
      password,
      branchId,
      roleId,
      scopes,
      roles,
      branches,
      loading,
      error,
      errors,
    } = this.state;

    return (
      <form className="mform" onSubmit={this.handleSubmit}>
        {!id && (
          <FormSection title={strings.user} icon="nc-icon nc-single-02">
            <FormGrid>
              <TextField
                label={strings.firstname}
                required
                value={firstName}
                onChange={(value) => this.setState({ firstName: value })}
                error={errors.firstName}
              />
              <TextField
                label={strings.lastname}
                required
                value={lastName}
                onChange={(value) => this.setState({ lastName: value })}
                error={errors.lastName}
              />
            </FormGrid>
            <FormGrid>
              <TextField
                label={strings.email}
                required
                value={email}
                onChange={(value) => this.setState({ email: value })}
                error={errors.email}
              />
              <TextField
                label={strings.phone}
                required
                value={phoneNumber}
                onChange={(value) => this.setState({ phoneNumber: value })}
                error={errors.phoneNumber}
              />
            </FormGrid>
            <FormGrid full>
              <TextField
                label={strings.password}
                type="password"
                required
                value={password}
                onChange={(value) => this.setState({ password: value })}
                error={errors.password}
                help={strings.password_hint}
              />
            </FormGrid>
          </FormSection>
        )}

        {id && (
          <FormSection title={strings.user} icon="nc-icon nc-single-02">
            <FormGrid>
              <TextField label={strings.name} value={`${firstName} ${lastName}`} disabled onChange={() => {}} />
              <TextField label={strings.email} value={email} disabled onChange={() => {}} />
            </FormGrid>
          </FormSection>
        )}

        <FormSection title={strings.access} icon="nc-icon nc-key-25">
          <FormGrid>
            <SelectField
              label={strings.role}
              value={roleId}
              options={roles}
              onChange={this.handleRoleSelect}
              placeholder={strings.select}
              help={strings.role_prefill_hint}
            />
            <SelectField
              label={strings.garage}
              value={branchId}
              options={branches}
              onChange={(value) => this.setState({ branchId: value })}
              placeholder={strings.all_garages}
              help={strings.garage_pin_hint}
            />
          </FormGrid>

          <Field error={errors.scopes}>
            <PermissionMatrix
              value={scopes}
              onChange={(value) => this.setState({ scopes: value })}
            />
          </Field>
        </FormSection>

        <FormAlert>{error}</FormAlert>
        <FormActions onCancel={this.props.onCancel} loading={loading} />
      </form>
    );
  }
}
